// src/pages/OrganisationVerificationDetails.js
import {
  Row,
  Col,
  Card,
  Button,
  Typography,
  Image,
  Space,
  Tag,
  Descriptions,
  Modal,
  Form,
  Input,
  message,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { TextArea } = Input;

function OrganisationVerificationDetails() {
  const { organisationId } = useParams();
  const navigate = useNavigate();
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (organisationId) {
      fetchVerificationDetails();
    }
  }, [organisationId]);

  const fetchVerificationDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/organisation-verification/${organisationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Erreur lors du chargement");
      }

      const data = await response.json();
      setVerification(data.data);
    } catch (error) {
      message.error("Erreur lors du chargement des détails");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (values) => {
    try {
      setActionLoading(true);
      const payload = {
        status: actionType,
        ...(actionType === "rejected" && {
          rejection_reason: values.rejection_reason,
        }),
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/organisation-verification/${organisationId}/update-status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error("Erreur lors du traitement");
      }

      message.success(
        `Demande ${actionType === "approved" ? "approuvée" : "rejetée"} avec succès`,
      );
      setModalVisible(false);
      form.resetFields();
      fetchVerificationDetails();
    } catch (error) {
      message.error("Erreur lors du traitement de la demande");
      console.error(error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteDocuments = () => {
    Modal.confirm({
      title: "Confirmer la suppression",
      content:
        "Êtes-vous sûr de vouloir supprimer tous les documents de cette organisation ?",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/admin/organisation-verification/${organisationId}/delete-all`,
            {
              method: "DELETE",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            },
          );

          if (!response.ok) {
            throw new Error("Erreur lors de la suppression");
          }

          message.success("Documents supprimés avec succès");
          fetchVerificationDetails();
        } catch (error) {
          message.error("Erreur lors de la suppression");
          console.error(error);
        }
      },
    });
  };

  const openActionModal = (type) => {
    setActionType(type);
    setModalVisible(true);
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: "orange", text: "En attente" },
      approved: { color: "green", text: "Approuvé" },
      rejected: { color: "red", text: "Rejeté" },
      not_submitted: { color: "default", text: "Non soumis" },
    };
    const config = statusConfig[status] || statusConfig.not_submitted;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  if (loading || !verification) {
    return (
      <Card loading={true}>
        <div style={{ height: "400px" }} />
      </Card>
    );
  }

  const allDocumentsUploaded = !!(verification.rccmUrl && verification.dfeUrl);

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col xs={24} className="mb-24">
            <Card bordered={false} className="criclebox cardbody h-full">
              <div className="project-ant">
                <div>
                  <Button
                    type="link"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate("/organisation-verification")}
                  >
                    Retour à la liste
                  </Button>
                  <Title level={5}>Détails de la vérification KYB</Title>
                  <Text>
                    Organisation:{" "}
                    <strong>{verification.organisation?.name}</strong>
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
        <Row gutter={[24, 0]}>
          {/* Informations de l'organisation */}
          <Col xs={24} md={12} className="mb-24">
            <Card
              bordered={false}
              className="criclebox h-full"
              title="Informations de l'organisation"
            >
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Nom de l'organisation">
                  {verification.organisation?.name}
                </Descriptions.Item>
                <Descriptions.Item label="ID Organisation">
                  <Text copyable>{verification.organisationId}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Type de compte">
                  <Tag>{verification.organisation?.accountType}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Niveau">
                  <Tag
                    color={
                      verification.organisation?.level === "level_2"
                        ? "green"
                        : "default"
                    }
                  >
                    {verification.organisation?.level === "level_2"
                      ? "Niveau 2 (Vérifié)"
                      : "Niveau 1"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Statut de l'organisation">
                  <Tag
                    color={
                      verification.organisation?.status === "active"
                        ? "green"
                        : "red"
                    }
                  >
                    {verification.organisation?.status}
                  </Tag>
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              <Title level={5}>Propriétaire</Title>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Nom complet">
                  {verification.organisation?.owner?.fullname}
                </Descriptions.Item>
                <Descriptions.Item label="Téléphone">
                  {verification.organisation?.owner?.phone}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {verification.organisation?.owner?.email || "Non renseigné"}
                </Descriptions.Item>
                <Descriptions.Item label="ID Utilisateur">
                  <Text copyable>{verification.owner}</Text>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          {/* Statut de vérification */}
          <Col xs={24} md={12} className="mb-24">
            <Card
              bordered={false}
              className="criclebox h-full"
              title="Statut de vérification"
            >
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Statut actuel">
                  {getStatusTag(verification.status)}
                </Descriptions.Item>
                <Descriptions.Item label="Nombre de rejets">
                  <Tag
                    color={
                      verification.rejectionCount >= 3
                        ? "red"
                        : verification.rejectionCount > 0
                          ? "orange"
                          : "default"
                    }
                  >
                    {verification.rejectionCount}/3
                  </Tag>
                  {verification.rejectionCount >= 3 && (
                    <Tag color="red" style={{ marginLeft: 8 }}>
                      Limite atteinte
                    </Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Date de soumission">
                  {verification.submittedAt
                    ? new Date(verification.submittedAt).toLocaleString("fr-FR")
                    : "Non soumis"}
                </Descriptions.Item>
                <Descriptions.Item label="Date de révision">
                  {verification.reviewedAt
                    ? new Date(verification.reviewedAt).toLocaleString("fr-FR")
                    : "Non révisé"}
                </Descriptions.Item>
                <Descriptions.Item label="Documents">
                  <Space>
                    {verification.rccmUrl && (
                      <Tag color="success" icon={<CheckCircleOutlined />}>
                        RCCM
                      </Tag>
                    )}
                    {verification.dfeUrl && (
                      <Tag color="success" icon={<CheckCircleOutlined />}>
                        DFE
                      </Tag>
                    )}
                    {!verification.rccmUrl && !verification.dfeUrl && (
                      <Tag color="default">Aucun document</Tag>
                    )}
                  </Space>
                </Descriptions.Item>
              </Descriptions>

              {verification.rejectionReason && (
                <>
                  <Divider />
                  <Title level={5} style={{ color: "#ff4d4f" }}>
                    Motif de rejet
                  </Title>
                  <Card
                    size="small"
                    style={{
                      backgroundColor: "#fff2e8",
                      borderColor: "#ff4d4f",
                    }}
                  >
                    <Text>{verification.rejectionReason}</Text>
                  </Card>
                </>
              )}

              <Divider />

              <Title level={5}>Documents soumis</Title>
              <Space direction="vertical" style={{ width: "100%" }}>
                <div>
                  <Text strong>
                    <FileTextOutlined /> RCCM:{" "}
                  </Text>
                  {verification.rccmUrl ? (
                    <Tag color="success">Soumis</Tag>
                  ) : (
                    <Tag color="default">Non soumis</Tag>
                  )}
                </div>
                <div>
                  <Text strong>
                    <FileTextOutlined /> DFE:{" "}
                  </Text>
                  {verification.dfeUrl ? (
                    <Tag color="success">Soumis</Tag>
                  ) : (
                    <Tag color="default">Non soumis</Tag>
                  )}
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
        {/* Documents */}
        <Row gutter={[24, 0]}>
          <Col xs={24} className="mb-24">
            <Card
              bordered={false}
              className="criclebox"
              title="Documents administratifs"
            >
              <Row gutter={[24, 24]}>
                {/* RCCM */}
                <Col xs={24} md={12}>
                  <Card
                    type="inner"
                    title={
                      <Space>
                        <FileTextOutlined />
                        <span>RCCM (Registre du Commerce)</span>
                      </Space>
                    }
                    extra={
                      verification.rccmUrl && (
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          href={verification.rccmUrl}
                          target="_blank"
                        >
                          Télécharger
                        </Button>
                      )
                    }
                  >
                    {verification.rccmUrl ? (
                      <Image.PreviewGroup>
                        <Image
                          src={verification.rccmUrl}
                          alt="RCCM"
                          style={{ width: "100%", borderRadius: "8px" }}
                        />
                      </Image.PreviewGroup>
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          backgroundColor: "#fafafa",
                          borderRadius: "8px",
                        }}
                      >
                        <FileTextOutlined
                          style={{ fontSize: "48px", color: "#d9d9d9" }}
                        />
                        <div style={{ marginTop: "16px", color: "#999" }}>
                          Aucun document soumis
                        </div>
                      </div>
                    )}
                  </Card>
                </Col>

                {/* DFE */}
                <Col xs={24} md={12}>
                  <Card
                    type="inner"
                    title={
                      <Space>
                        <FileTextOutlined />
                        <span>DFE (Déclaration Fiscale d'Existence)</span>
                      </Space>
                    }
                    extra={
                      verification.dfeUrl && (
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          href={verification.dfeUrl}
                          target="_blank"
                        >
                          Télécharger
                        </Button>
                      )
                    }
                  >
                    {verification.dfeUrl ? (
                      <Image.PreviewGroup>
                        <Image
                          src={verification.dfeUrl}
                          alt="DFE"
                          style={{ width: "100%", borderRadius: "8px" }}
                        />
                      </Image.PreviewGroup>
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "40px",
                          backgroundColor: "#fafafa",
                          borderRadius: "8px",
                        }}
                      >
                        <FileTextOutlined
                          style={{ fontSize: "48px", color: "#d9d9d9" }}
                        />
                        <div style={{ marginTop: "16px", color: "#999" }}>
                          Aucun document soumis
                        </div>
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
        // src/pages/OrganisationVerificationDetails.js // Modifier la condition
        pour afficher les actions :
        {/* Actions - Afficher tant que reject_count < 3 */}
        {verification.rejectionCount < 3 &&
          verification.status !== "approved" && (
            <Row gutter={[24, 0]}>
              <Col xs={24} className="mb-24">
                <Card
                  bordered={false}
                  className="criclebox"
                  title="Actions administrateur"
                >
                  {verification.status === "rejected" && (
                    <div
                      style={{
                        marginBottom: 16,
                        padding: 12,
                        backgroundColor: "#fff7e6",
                        borderLeft: "3px solid #fa8c16",
                        borderRadius: 4,
                      }}
                    >
                      <Text type="warning">
                        Cette demande a été rejetée{" "}
                        {verification.rejectionCount} fois. L'organisation peut
                        soumettre à nouveau ses documents.
                        {verification.rejectionCount === 2 &&
                          " ⚠️ Dernière tentative possible !"}
                      </Text>
                    </div>
                  )}

                  <Space size="large">
                    <Button
                      type="primary"
                      size="large"
                      icon={<CheckCircleOutlined />}
                      onClick={() => openActionModal("approved")}
                      disabled={!allDocumentsUploaded}
                    >
                      Approuver la demande
                    </Button>
                    <Button
                      danger
                      size="large"
                      icon={<CloseCircleOutlined />}
                      onClick={() => openActionModal("rejected")}
                      disabled={verification.rejectionCount >= 3}
                    >
                      Rejeter la demande
                    </Button>
                    <Button
                      size="large"
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteDocuments}
                    >
                      Supprimer les documents
                    </Button>
                  </Space>

                  {!allDocumentsUploaded && (
                    <div
                      style={{
                        marginTop: 16,
                        padding: 12,
                        backgroundColor: "#e6f7ff",
                        borderLeft: "3px solid #1890ff",
                        borderRadius: 4,
                      }}
                    >
                      <Text type="secondary">
                        <FileTextOutlined /> Tous les documents doivent être
                        soumis avant de pouvoir approuver la demande.
                      </Text>
                    </div>
                  )}

                  {verification.rejectionCount >= 3 && (
                    <div
                      style={{
                        marginTop: 16,
                        padding: 12,
                        backgroundColor: "#fff1f0",
                        borderLeft: "3px solid #ff4d4f",
                        borderRadius: 4,
                      }}
                    >
                      <Text type="danger">
                        ⛔ Cette organisation a atteint la limite de 3 rejets.
                        Elle doit contacter le support pour débloquer sa
                        situation.
                      </Text>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          )}
        {verification.status === "approved" && (
          <Row gutter={[24, 0]}>
            <Col xs={24} className="mb-24">
              <Card bordered={false} className="criclebox">
                <div
                  style={{
                    padding: 16,
                    backgroundColor: "#f6ffed",
                    borderLeft: "3px solid #52c41a",
                    borderRadius: 4,
                    textAlign: "center",
                  }}
                >
                  <CheckCircleOutlined
                    style={{ fontSize: 48, color: "#52c41a", marginBottom: 16 }}
                  />
                  <Title
                    level={4}
                    style={{ color: "#52c41a", marginBottom: 8 }}
                  >
                    Demande approuvée
                  </Title>
                  <Text>
                    Cette organisation a été vérifiée avec succès et est passée
                    au Niveau 2.
                  </Text>
                </div>
              </Card>
            </Col>
          </Row>
        )}
      </div>

      {/* Modal d'action */}
      <Modal
        title={
          actionType === "approved"
            ? "Approuver la demande de vérification"
            : "Rejeter la demande de vérification"
        }
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={handleAction} layout="vertical">
          {actionType === "approved" ? (
            <div
              style={{
                padding: 16,
                backgroundColor: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: 8,
                marginBottom: 16,
              }}
            >
              <CheckCircleOutlined
                style={{ color: "#52c41a", fontSize: 24, marginRight: 8 }}
              />
              <Text strong>Confirmation d'approbation</Text>
              <div style={{ marginTop: 8 }}>
                <Text>
                  Vous êtes sur le point d'approuver cette demande.
                  L'organisation passera au <strong>Niveau 2 (Vérifié)</strong>.
                </Text>
              </div>
            </div>
          ) : (
            <Form.Item
              name="rejection_reason"
              label="Motif de rejet"
              rules={[
                {
                  required: true,
                  message: "Veuillez indiquer le motif de rejet",
                },
                {
                  min: 10,
                  message: "Le motif doit contenir au moins 10 caractères",
                },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="Expliquez clairement pourquoi la demande est rejetée (documents illisibles, informations incorrectes, etc.)"
              />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={actionLoading}
                icon={
                  actionType === "approved" ? (
                    <CheckCircleOutlined />
                  ) : (
                    <CloseCircleOutlined />
                  )
                }
              >
                {actionType === "approved"
                  ? "Confirmer l'approbation"
                  : "Confirmer le rejet"}
              </Button>
              <Button onClick={() => setModalVisible(false)}>Annuler</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default OrganisationVerificationDetails;
